import React, { useCallback, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import throttle from 'lodash.throttle'

import Button from 'components/Button'
import { Flex } from 'components/Box'
import Modal from 'components/Modal'

import copy from 'copy-to-clipboard'
import ToastContext from 'containers/ToastManager/ToastContext'

const TOAST_TIME_OF_LIFE = 5000
const THROTTLE_SETTINGS = { trailing: false }

const TokenWrapper = styled.p`
  /* Increase the style specificity to override existing selectors */
  ${/* sc-selector */ Modal.Body} & {
    margin: 0 0 12px;
  }

  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  padding: 14px 0px; /* stylelint-disable-line scales/space */
  background: ${({ theme }) => theme.colors.background.light.secondary};
  border-radius: 4px;
  box-shadow: ${({ theme }) => theme.shadows.boxShadow.subtle};
  border: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.black};
  text-align: center;
`

/**
 * GeneratedTokenModal
 *
 * This component shows a modal to the user with the new generated token.
 * This component is only representative, therefore no create token action happens here,
 * it's only presented.
 *
 * To properly work this component should be call through the ModalContext API.
 *
 * @param {Object} props - Component props.
 * @param {string} props.token - The full token digits.
 * @param {function} props.onCancel - Even listener of the hide modal.
 */
const GeneratedTokenModal = ({ token, onCancel }) => {
  const { showToast } = useContext(ToastContext)
  const showToastPreventive = useMemo(
    () => throttle(showToast, TOAST_TIME_OF_LIFE, THROTTLE_SETTINGS),
    [showToast]
  )

  const handleCopyClipboard = useCallback(() => {
    copy(token)
    showToastPreventive('Key copied to clipboard', 'positive')
  }, [token, showToastPreventive])

  return (
    <Modal onCancel={onCancel}>
      <Modal.Header>
        <span>New API key generated</span>
      </Modal.Header>
      <Modal.Body>
        <Flex flexDirection="column">
          <p>
            {'We’ll'} only show this key <strong>once</strong> so make sure to
            copy it to your clipboard now
          </p>
          <TokenWrapper>{token}</TokenWrapper>
          <Button fill onClick={handleCopyClipboard}>
            Copy to clipboard
          </Button>
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onCancel}>Close</Button>
      </Modal.Footer>
    </Modal>
  )
}

GeneratedTokenModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
}

export default GeneratedTokenModal
